import React, { useEffect, useState } from "react";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";

import MarketingHero from "components/marketing/MarketingHero";
import CenterLoading from "../../components/core/CenterLoading";
import { getAutocompleteType } from "../../components/contact/ContactForm";
import users, { ResetPasswordDto } from "../../services/users";
import { useTitle } from "../../utils/UseTitleUtils";

interface ResetPasswordProps {}
type ResetPasswordParams = {
  user_id?: string;
};

type ResetPasswordInput = {
  password: string;
  confirmPassword: string;
};

type ResetPasswordError = Partial<ResetPasswordInput>;

const defaultInput: ResetPasswordInput = {
  password: "",
  confirmPassword: "",
};

const ResetPassword = (props: ResetPasswordProps) => {
  const [inputs, setInputs] = useState<ResetPasswordInput>(defaultInput);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ResetPasswordError>({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const params = useParams<ResetPasswordParams>();
  const navigate = useNavigate();

  useTitle("Change Password");

  const sentForgotPassword = async () => {
    if (!params.user_id) {
      return;
    }

    setIsLoading(true);
    try {
      await users.getResetUserDetail(params.user_id) as ResetPasswordDto;
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const resetPassword = async () => {
    setIsLoading(true);
    try {
      await users.setResetPassword({
        password: inputs.password,
        confirmPassword: inputs.confirmPassword,
        resetPasswordToken: params.user_id,
      });

      navigate("/auth/login", {
        replace: true,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorId = (field: keyof ResetPasswordError) =>
    errors[field] ? `reset-password-${field}-error` : undefined;

  const errorMessage = (field: keyof ResetPasswordError) => {
    if (!errors[field]) {
      return null;
    }

    return (
      <div
        id={getErrorId(field)}
        className="marketing-page__error"
        role="alert"
      >
        {errors[field]}
      </div>
    );
  };

  const handleChange =
    (fieldName: keyof ResetPasswordInput) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputs((current) => ({
        ...current,
        [fieldName]: event.target.value,
      }));
      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors.password;
        delete nextErrors.confirmPassword;
        return nextErrors;
      });
    };

  const onResetPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: ResetPasswordError = {};

    if (!inputs.password.trim()) {
      nextErrors.password = "Password is required.";
    }

    if (!inputs.confirmPassword.trim()) {
      nextErrors.confirmPassword = "Confirm password is required.";
    } else if (inputs.password !== inputs.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    if (params.user_id) {
      resetPassword();
    }
  };

  useEffect(() => {
    sentForgotPassword();
  }, []);

  return (
    <div className="marketing-page marketing-page--reset-password">
      <CenterLoading isVisible={isLoading} />

      <main className="marketing-page__main">
        <MarketingHero title="Change Password" />

        <section className="marketing-page__section">
          <div className="marketing-page__section-inner marketing-page__section-inner--content">
            <div className="marketing-page__form-panel marketing-page__form-panel--login">
              <div className="marketing-page__copy-stack">
                <p>Choose a new password for your account.</p>
              </div>

              <form className="marketing-page__form" onSubmit={onResetPassword}>
                <div className="marketing-page__field">
                  <label className="marketing-page__label" htmlFor="reset-password">
                    Password
                  </label>
                  <div className="input-group">
                    <input
                      id="reset-password"
                      className="marketing-page__input form-control"
                      type={passwordVisible ? "text" : "password"}
                      required
                      autoComplete={getAutocompleteType("password")}
                      value={inputs.password}
                      onChange={handleChange("password")}
                      aria-invalid={Boolean(errors.password)}
                      aria-describedby={getErrorId("password")}
                    />
                    <button
                      className="input-group-text"
                      aria-label="Toggle password visibility"
                      type="button"
                      onClick={() => setPasswordVisible((current) => !current)}
                    >
                      <FontAwesomeIcon
                        icon={passwordVisible ? faEyeSlash : faEye}
                      />
                    </button>
                  </div>
                  {errorMessage("password")}
                </div>

                <div className="marketing-page__field">
                  <label
                    className="marketing-page__label"
                    htmlFor="reset-confirm-password"
                  >
                    Confirm Password
                  </label>
                  <div className="input-group">
                    <input
                      id="reset-confirm-password"
                      className="marketing-page__input form-control"
                      type={confirmPasswordVisible ? "text" : "password"}
                      required
                      autoComplete={getAutocompleteType("confirmPassword")}
                      value={inputs.confirmPassword}
                      onChange={handleChange("confirmPassword")}
                      aria-invalid={Boolean(errors.confirmPassword)}
                      aria-describedby={getErrorId("confirmPassword")}
                    />
                    <button
                      className="input-group-text"
                      aria-label="Toggle confirm password visibility"
                      type="button"
                      onClick={() =>
                        setConfirmPasswordVisible((current) => !current)
                      }
                    >
                      <FontAwesomeIcon
                        icon={confirmPasswordVisible ? faEyeSlash : faEye}
                      />
                    </button>
                  </div>
                  {errorMessage("confirmPassword")}
                </div>

                <button
                  title="Reset password"
                  aria-label="Reset Password"
                  type="submit"
                  className="marketing__button marketing__button--primary marketing-page__full-button"
                >
                  Reset Password
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ResetPassword;
